import { labelForMetric } from '@chiroup/core/constants/metricCategories';
import { X } from 'lucide-react';
import React, { useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Granularity } from '../../services/eventLog.service';
import LineChart from './charts/LineChart';
import { computeTrend, formatTrend, trendArrow, trendColor } from './utils/trend';

type Props = {
  open: boolean;
  onClose: () => void;
  metricKey: string | null;
  value: number;
  previousValue: number;
  dailySeries?: number[];
  dates?: string[];
  granularity?: Granularity;
};

const MetricChartModal: React.FC<Props> = ({
  open,
  onClose,
  granularity,
  metricKey,
  value,
  previousValue,
  dailySeries,
  dates,
}) => {
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', handler);

    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  const chartData = useMemo(() => {
    if (!dailySeries || !dates) return [];

    return dates.map((date, i) => ({
      date,
      total: dailySeries[i] ?? 0,
    }));
  }, [dailySeries, dates]);

  const trend = computeTrend(value, previousValue);

  if (!open || !metricKey) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden="true" />
      <div className="relative w-full max-w-4xl bg-white rounded-2xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{labelForMetric(metricKey)}</h3>
            <div className="flex items-center gap-3 mt-0.5">
              <span className="text-2xl font-semibold tabular-nums">{value.toLocaleString()}</span>
              <span className={`text-sm ${trendColor(trend.direction)}`}>
                {trendArrow(trend.direction)} {formatTrend(trend)}
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 rounded p-1 -m-1"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="p-6">
          {chartData.length > 0 ? (
            <LineChart data={chartData} height={320} granularity={granularity} />
          ) : (
            <div className="h-80 flex items-center justify-center text-sm text-gray-400">
              No daily data available
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default MetricChartModal;
